import { Box } from '@mui/material';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { ColumnsOnlyToolbar, DataGrid, DataGridLayout } from '@verantos-dev/shared-components';

import type { AnalyticalDatasetResultRow } from '../../api/types';
import {
  RESULTS_DEFAULT_SORT_MODEL,
  useAnalyticalDatasetResults,
  type PendingResultsColumn,
} from '../../hooks/useAnalyticalDatasetResults';
import { useVariableSourceList } from '../../hooks/useVariableSourceList';
import type { SelectedDatasetSource } from '../../types';

interface AnalyticalDatasetResultsProps {
  projectId: string;
  analyticalDatasetProfileId: string;
  /** Whether the dataset's definition has any analytical variable; the
   * demographic columns are hidden by default when it does. */
  hasAnalyticalVariables?: boolean;
  /** Definition sources (saved or staged) the processed run has no column
   * for; each is appended as an empty column. */
  sourcesMissingFromRun?: SelectedDatasetSource[];
}

const NO_SOURCES: SelectedDatasetSource[] = [];

export const AnalyticalDatasetResults = ({
  projectId,
  analyticalDatasetProfileId,
  hasAnalyticalVariables = false,
  sourcesMissingFromRun = NO_SOURCES,
}: AnalyticalDatasetResultsProps) => {
  const { formatMessage } = useIntl();
  const { rows, isLoading } = useVariableSourceList(sourcesMissingFromRun);
  const pendingColumns = useMemo<PendingResultsColumn[]>(
    () =>
      rows
        .filter((row) => row.status !== 'default')
        .map((row) => ({ key: row.key, title: isLoading && row.avId !== null ? '' : row.title })),
    [rows, isLoading]
  );

  const {
    mapServerColumns,
    useFetchResultsGridData,
    columnVisibilityModel,
    onColumnVisibilityModelChange,
  } = useAnalyticalDatasetResults({
    projectId,
    analyticalDatasetProfileId,
    hasAnalyticalVariables,
    pendingColumns,
    pendingCaption: formatMessage({ id: 'analysisDetail.tables.preview.resultsColumnPending' }),
  });

  return (
    <Box
      // minHeight matches DataGridLayout's floor so a short viewport scrolls the
      // page instead of squashing the grid.
      sx={{ width: '100%', flex: 1, minHeight: 400, display: 'flex', flexDirection: 'column' }}
      data-testid="analysis-results-dataset-grid"
    >
      {/* No maxHeight: the layout flexes to its container's height (its own
          400px minHeight stays as the floor) and the grid scrolls inside. */}
      <DataGridLayout id="analysis-results-dataset-grid" sx={{ width: '100%', flex: 1 }}>
        <DataGrid
          id="analysis-results-dataset-grid"
          columns={mapServerColumns}
          getRowId={(row: AnalyticalDatasetResultRow) => String(row.patient_id)}
          mode="server"
          useFetchDataGridData={useFetchResultsGridData}
          getRowCount={(data) => data.pagination.total}
          pagination
          loadOnFetch
          disableSearchParamSync
          // Opt-in shared-grid behaviors for server-side filtering.
          // Multi-column filtering stays on — the backend ANDs/ORs all
          // comma-joined `field:operator(value)` items.
          resetPageOnFilterChange
          serializeDateFilterValues
          initialPageSize={500}
          pageSizeOptions={[25, 50, 100, 250, 500]}
          initialState={{
            sorting: { sortModel: RESULTS_DEFAULT_SORT_MODEL },
          }}
          // Demographic columns are hidden by default when the dataset has
          // analytical variables; toggles from the columns panel are kept in
          // the hook.
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={onColumnVisibilityModelChange}
          // Boolean filter Value dropdown labels, matching the title-cased
          // cell rendering (True/False). Display-only — the filter item value
          // (and thus the serialized wire format) stays true/false.
          localeText={{
            filterValueAny: 'Any',
            filterValueTrue: 'True',
            filterValueFalse: 'False',
          }}
          slots={{ toolbar: ColumnsOnlyToolbar }}
          slotProps={{
            baseSelect: {
              MenuProps: {
                PaperProps: { sx: { maxHeight: 320 } },
              },
            },
            panel: {
              sx: {
                // Filter panel: line the rows up as a table — consistent field
                // widths across rows (the hidden And/Or slot on row 1 already
                // reserves its column), padded rows, aligned baselines.
                '& .MuiDataGrid-filterForm': {
                  padding: 3,
                  gap: 3,
                  alignItems: 'flex-end',
                },
                '& .MuiDataGrid-filterFormLogicOperatorInput': { minWidth: 64 },
                '& .MuiDataGrid-filterFormColumnInput': { width: 200 },
                '& .MuiDataGrid-filterFormOperatorInput': { width: 150 },
                '& .MuiDataGrid-filterFormValueInput': { width: 200 },
                '& .MuiDataGrid-panelFooter': {
                  paddingLeft: 3,
                  paddingRight: 3,
                  paddingBottom: 2,
                },
                '& .MuiDataGrid-columnsManagementHeader': {
                  paddingLeft: 5,
                  paddingRight: 5,
                  paddingTop: 4,
                },
                '& .MuiDataGrid-columnsManagement': { paddingLeft: 5, paddingRight: 5 },
                '& .MuiDataGrid-columnsManagementRow': { marginLeft: 0, marginRight: 0 },
                '& .MuiDataGrid-columnsManagementRow .MuiCheckbox-root': { paddingLeft: 0 },
                '& .MuiDataGrid-columnsManagementFooter .MuiFormControlLabel-root': {
                  marginLeft: 0,
                },
                '& .MuiDataGrid-columnsManagementFooter .MuiCheckbox-root': { paddingLeft: 0 },
                '& .MuiDataGrid-columnsManagementFooter': {
                  paddingLeft: 5,
                  paddingRight: 5,
                  paddingTop: 4,
                  paddingBottom: 4,
                },
              },
            },
          }}
        />
      </DataGridLayout>
    </Box>
  );
};
